import { useLocalStorage } from '@gravitate-js/excalibrr'
import moment, { Moment } from 'moment'
import { useState } from 'react'

import type { InvoiceGroup } from '../../../api/useFreight/types'

export type CostRevenueType = 'All' | 'Cost' | 'Revenue'

export const getTimeDisplay = (value: string | null) => {
  if (!value) return { display: '-', isWarning: false }

  const timeDifference = moment().diff(moment(value), 'hours')
  let isWarning = false
  let differenceDisplay: string

  if (timeDifference > 0) {
    if (timeDifference >= 24) {
      differenceDisplay = `${moment().diff(moment(value), 'days')}d ago`
    } else if (timeDifference >= 1) {
      differenceDisplay = `${Math.floor(timeDifference)}h ago`
    } else {
      differenceDisplay = 'Just now'
    }
    isWarning = timeDifference > 36 // Mark as warning if more than 36 hours
  } else {
    // Future date
    const absTimeDiff = Math.abs(timeDifference)
    if (absTimeDiff >= 24) {
      differenceDisplay = `In ${Math.abs(moment().diff(moment(value), 'days'))}d`
    } else {
      differenceDisplay = `In ${Math.abs(Math.floor(timeDifference))}h`
    }
  }

  return { display: differenceDisplay, isWarning }
}
export function useGridFilters() {
  // Status filter from localStorage
  const { value: activeStatus, setValue: setActiveStatus } = useLocalStorage<string>('freight-invoices-status', 'all')

  // Cost/Revenue filter from localStorage
  const { value: costRevenue, setValue: setCostRevenue } = useLocalStorage<CostRevenueType>(
    'freight-invoices-cost-revenue',
    'All'
  )

  // Handler for date range changes

  const statusMap = {
    open: 'open',
    cr_approval: 'cr_approval',
    blocked: 'blocked',
    failure: 'failure',
    needs_attention: 'needs_attention',
    hold: 'hold',
    ready: 'ready',
    sent: 'sent',
  }
  // Function to filter invoice groups based on all criteria
  const getFilteredData = (invoiceGroups: InvoiceGroup[] | undefined) => {
    if (!invoiceGroups || !Array.isArray(invoiceGroups)) return []

    let filtered = invoiceGroups

    // Filter by status
    if (activeStatus !== 'all') {
      // Map of URL-friendly keys (with dashes) to actual status values (with spaces)

      filtered = filtered.filter((group) => {
        // check if any VALUES of the status map match the status, but then, use the key for the counts
        const targetStatus = statusMap[activeStatus as keyof typeof statusMap]

        return targetStatus === group.status
      })
    } else {
      // For "All Open", exclude Sent items
      filtered = filtered.filter((group) => group.status !== 'sent')
    }

    // Filter by cost/revenue
    if (costRevenue !== 'All') {
      filtered = filtered.filter((group) => group.book_type === costRevenue)
    }

    return filtered
  }

  // Calculate status counts for the filtered data
  const getStatusCounts = (invoiceGroups: InvoiceGroup[] | undefined) => {
    if (!invoiceGroups || !Array.isArray(invoiceGroups)) return { all: 0 }

    const counts: Record<string, number> = {}

    // Filter invoices by cost/revenue first
    const filteredInvoices =
      costRevenue === 'All' ? invoiceGroups : invoiceGroups.filter((group) => group.book_type === costRevenue)

    // Count all open items (excluding Sent)
    counts.all = filteredInvoices.filter((group) => group.status !== 'sent').length

    // Count by status
    for (const group of filteredInvoices) {
      if (group.status) {
        // check if any VALUES of the status map match the status, but then, use the key for the counts
        const statusKey = Object.keys(statusMap).find(
          (key) => statusMap[key as keyof typeof statusMap] === group.status
        )

        if (statusKey) {
          counts[statusKey] = (counts[statusKey] || 0) + 1
        }
      }
    }

    return counts
  }

  // Utility function for time display

  return {
    // Filter state and setters
    activeStatus,
    setActiveStatus,
    costRevenue,
    setCostRevenue,

    // Filter application functions
    getFilteredData,
    getStatusCounts,

    // Utility functions
    getTimeDisplay,
  }
}
